import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMe, useSettings, useUpdateSettings } from "../api/hooks";
import { Button, ErrorBox, Field, Loading, Page } from "../components/ui";

export const Route = createFileRoute("/settings")({ component: Settings });

function Settings() {
  const me = useMe();
  const settings = useSettings();
  const update = useUpdateSettings();
  const [error, setError] = useState<unknown>(null);
  // Managers see only their own card; the organisation's controls are an admin's to change.
  const isAdmin = me.data?.roles.some((r) => r === "hr_admin" || r === "platform_admin") ?? false;
  return (
    <Page title="Settings" sub={isAdmin ? "Privacy and data choices for this organisation." : "Your sign-in and preferences."}>
      {error != null && <div className="mb-3"><ErrorBox error={error} /></div>}
      <div className="grid grid-cols-2 gap-3">
        {isAdmin && <div className="card">
          <h3 className="font-bold text-[15px]">Meeting intelligence</h3>
          <p className="text-[13px] text-muted mt-1">When on, Humindly ingests the structured summaries your meeting tools already produce (topics, next steps) for 1:1 context. Never recordings, never transcript text. Off by default.</p>
          {settings.isPending ? (
            <Loading />
          ) : settings.isError ? (
            <ErrorBox error={settings.error} />
          ) : (
            <label className="flex items-center gap-3 mt-4 text-[13px] font-semibold">
              <input type="checkbox" checked={settings.data.meeting_intelligence_opt_in} disabled={update.isPending} onChange={(e) => update.mutate({ meeting_intelligence_opt_in: e.target.checked }, { onError: setError })} />
              Meeting summaries opt-in {settings.data.meeting_intelligence_opt_in ? "— on" : "— off"}
            </label>
          )}
        </div>}
        {isAdmin && <FloorAndRetention />}
        <div className="card">
          <h3 className="font-bold text-[15px]">You</h3>
          <div className="text-[13px] mt-2"><span className="text-muted">Signed in as</span> {me.data?.email}</div>
          <div className="text-[13px]"><span className="text-muted">Roles</span> {me.data?.roles.join(", ")}</div>
          <div className="text-[13px]"><span className="text-muted">Tenant</span> <code className="text-[12px]">{me.data?.tenant_id}</code></div>
          <Button variant="light" className="mt-3" onClick={() => window.location.reload()}>Refresh</Button>
        </div>
      </div>
    </Page>
  );
}

function FloorAndRetention() {
  const settings = useSettings();
  const update = useUpdateSettings();
  const [floor, setFloor] = useState<string>("");
  const [days, setDays] = useState<string>("");
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const current = settings.data;
  const floorValue = floor === "" ? String(current?.wi_min_population ?? 5) : floor;
  const daysValue = days === "" ? String(current?.data_retention_days ?? 400) : days;
  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        setSaved(false);
        update.mutate({ wi_min_population: Number(floorValue), data_retention_days: Number(daysValue) }, { onError: setError, onSuccess: () => { setSaved(true); setFloor(""); setDays(""); } });
      }}
    >
      <h3 className="font-bold text-[15px]">Privacy floor and retention</h3>
      <p className="text-[13px] text-muted mt-1">Aggregate signals are only shown for groups of at least the floor — never below 5. Individual-level views exist only inside a manager's own reporting line. Derived data older than the retention window is purged nightly; Slack raw pages are kept 7 days regardless.</p>
      {settings.isPending ? <Loading /> : (
        <div className="grid grid-cols-2 gap-3 mt-4">
          <Field label="Floor (people)"><input className="input" type="number" min={5} max={100} value={floorValue} onChange={(e) => setFloor(e.target.value)} /></Field>
          <Field label="Retention (days)"><input className="input" type="number" min={30} max={1100} value={daysValue} onChange={(e) => setDays(e.target.value)} /></Field>
        </div>
      )}
      {error != null && <div className="mt-3"><ErrorBox error={error} /></div>}
      {saved && <div className="notice mt-3">Saved. Workforce applies the new floor immediately.</div>}
      <Button type="submit" className="mt-3" disabled={update.isPending || settings.isPending}>Save</Button>
    </form>
  );
}
